import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  api,
  APIError,
  isFeasibilityDetail,
  isQuotaError,
  type AnalysisReport,
  type AT,
  type ContentLang,
  type Course,
  type CourseSession,
  type FeasibilityReport,
  type ILO,
  type TLA,
  type WizardCatalog,
} from "@/api/client";
import ContentLangBanner from "@/components/ContentLangBanner";
import CreditCost from "@/components/CreditCost";
import Header from "@/components/Layout/Header";
import LoadingOverlay from "@/components/Loading";
import FeasibilityPanel from "@/components/FeasibilityPanel";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";
import TrainerGuideCard from "@/components/TrainerGuideCard";
import {
  useAtFormatLabels,
  useDateLocale,
  useModalityLabels,
  useSessionKindLabels,
} from "@/lib/labels";

function formatSessionDate(dateStr: string | null | undefined, locale: string): string {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// Konstruktions-Download — identisch zu CourseEditor.
async function downloadConstruction(
  courseId: string,
  format: "pdf" | "md",
  courseTitle: string,
): Promise<void> {
  const url =
    format === "pdf"
      ? api.exportConstructionPdfUrl(courseId)
      : api.exportConstructionMdUrl(courseId);
  const token = localStorage.getItem("ca_access_token");
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  const slug = (courseTitle || "konstruktion").replace(/[^a-zA-Z0-9]/g, "_").slice(0, 50);
  a.download = `${slug}_konstruktion.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default function CourseOverview() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const modalityLabels = useModalityLabels();
  const atFormatLabels = useAtFormatLabels();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [catalog, setCatalog] = useState<WizardCatalog | null>(null);
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [latestReport, setLatestReport] = useState<AnalysisReport | null>(null);
  const [feasibility, setFeasibility] = useState<FeasibilityReport | null>(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [quota, setQuota] = useState<unknown>(null);
  const [downloadBusy, setDownloadBusy] = useState<"pdf" | "md" | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getCourse(id).then(setCourse);
    api.wizardCatalog().then(setCatalog).catch(() => setCatalog(null));
    api.listSessions(id).then(setSessions).catch(() => setSessions([]));
    api.auditFeasibility(id).then(setFeasibility).catch(() => setFeasibility(null));
    api.listReports(id).then((reports) => {
      if (reports.length > 0) {
        const sorted = [...reports].sort(
          (a, b) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime(),
        );
        setLatestReport(sorted[0]);
      }
    }).catch(() => setLatestReport(null));
  }, [id]);

  const tlaLabel = useMemo(() => {
    const map = new Map((catalog?.tla_types || []).map((x) => [x.id, x.name]));
    return (tid: string | undefined) => (tid ? map.get(tid) || tid : "—");
  }, [catalog]);

  const atLabel = useMemo(() => {
    const map = new Map((catalog?.at_types || []).map((x) => [x.id, x.name]));
    return (tid: string | undefined) => (tid ? map.get(tid) || tid : "—");
  }, [catalog]);

  const formatLabel = useMemo(() => {
    const map = new Map((catalog?.course_formats || []).map((f) => [f.id, f.name]));
    return (fid: string | undefined | null) => (fid ? map.get(fid) || fid : null);
  }, [catalog]);

  if (!course) return <LoadingOverlay message={t("overview.loading")} />;

  const handleDownload = async (format: "pdf" | "md") => {
    setDownloadBusy(format);
    setDownloadError(null);
    try {
      await downloadConstruction(course.id, format, course.title);
    } catch (e) {
      setDownloadError(
        e instanceof Error && /^\d+$/.test(e.message)
          ? t("common.downloadFailedHttp", { status: e.message })
          : t("common.downloadFailed"),
      );
    } finally {
      setDownloadBusy(null);
    }
  };

  const runAudit = async () => {
    if (auditBusy) return; // Doppelklick-Sperre (Lauf dauert ~2 Minuten)
    setAuditBusy(true);
    try {
      const report = await api.runAudit(course.id);
      navigate(`/courses/${course.id}/audit/${report.id}`);
    } catch (err) {
      // 422 = Vollständigkeits-Gate: Mängelliste ins Panel statt alert().
      if (err instanceof APIError && err.status === 422 && isFeasibilityDetail(err.data)) {
        setFeasibility({ feasible: false, blockers: err.data.blockers, warnings: err.data.warnings ?? [] });
      } else if (isQuotaError(err)) {
        setQuota(err.data);
      } else {
        setDownloadError(err instanceof APIError ? err.detail : String(err));
      }
    } finally {
      setAuditBusy(false);
    }
  };

  const ilos = course.ilos || [];
  const tlas = course.tlas || [];
  const ats = course.ats || [];
  const ilosById = Object.fromEntries(ilos.map((i) => [i.id, i]));
  const editLink = (step: 1 | 2 | 3 | 4) => `/courses/${course.id}/edit?step=${step}`;
  // Sprache der Kursinhalte (Default „de") — für den Sprach-Hinweis-Banner.
  const contentLang: ContentLang =
    (course.metadata?.content_lang as ContentLang | undefined) ?? "de";

  return (
    <>
      <Header />
      {auditBusy && (
        <LoadingOverlay
          message={t("overview.auditBusy")}
          subMessage={t("overview.auditBusySub")}
        />
      )}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Course-Header */}
        <div className="mb-10">
          <div className="flex items-center gap-5">
            <Link
              to="/"
              className="text-xs font-bold uppercase tracking-widest text-ca-ink-muted hover:text-ca-accent-text transition"
            >
              {t("overview.backToList")}
            </Link>
            <Link
              to={`/courses/${course.id}/workspace`}
              className="inline-flex items-center gap-1.5 rounded-md border border-ca-accent/50 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-ca-accent-text transition hover:bg-ca-accent/10 hover:text-ca-accent-text"
            >
              {t("workspace.openLink")} →
            </Link>
          </div>
          <h1 className="text-4xl font-black text-ca-ink tracking-tighter uppercase italic mt-3 mb-2">
            {course.title}
          </h1>
          <p className="text-ca-ink-muted text-sm">
            {course.audience || t("overview.audienceOpen")}
            {formatLabel(course.format) && <> · {formatLabel(course.format)}</>}
            {course.duration_hours && <> · {course.duration_hours} h</>}
            {course.modality && <> · {modalityLabels[course.modality] || course.modality}</>}
          </p>
        </div>

        {/* Sprach-Hinweis: Kursinhalt ≠ UI-Sprache → Deep-Link in den
            Übersetzungs-Review-Flow im Editor (Tab „Veranstaltung") */}
        <ContentLangBanner
          contentLang={contentLang}
          onTranslate={() => navigate(`/courses/${course.id}/edit?step=1&translate=1`)}
        />

        {/* Top-Action-Bar */}
        <div className="mb-10 p-6 bg-ca-card rounded-2xl border border-ca-surface-border flex flex-wrap items-center gap-3">
          <span className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mr-2">
            {t("overview.saveConstruction")}
          </span>
          <button
            onClick={() => handleDownload("pdf")}
            disabled={downloadBusy !== null}
            className="px-4 py-2 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold hover:border-ca-accent hover:bg-ca-card disabled:opacity-40 transition"
          >
            {downloadBusy === "pdf" ? t("common.downloading") : t("overview.downloadPdf")}
          </button>
          <button
            onClick={() => handleDownload("md")}
            disabled={downloadBusy !== null}
            className="px-4 py-2 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold hover:border-ca-accent hover:bg-ca-card disabled:opacity-40 transition"
          >
            {downloadBusy === "md" ? t("common.downloading") : t("overview.downloadMd")}
          </button>
          <div className="flex-1" />
          <CreditCost feature="audit" className="text-xs text-ca-surface-muted" />
          <button
            onClick={runAudit}
            disabled={(feasibility ? !feasibility.feasible : ilos.length === 0 || tlas.length === 0 || ats.length === 0) || auditBusy}
            title={
              feasibility && !feasibility.feasible
                ? t("overview.auditBlockedTitle")
                : t("overview.auditStartTitle")
            }
            className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-ca-accent text-ca-brand text-xs font-black uppercase tracking-widest hover:bg-brand-gold-dark disabled:opacity-30 transition"
          >
            {auditBusy && (
              <span
                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-ca-brand/40 border-t-ca-brand"
                aria-hidden
              />
            )}
            {auditBusy ? t("workspace.auditRunning") : t("overview.auditStart")}
          </button>
        </div>
        {quota !== null && (
          <div className="mb-4">
            <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
          </div>
        )}
        {downloadError && (
          <p className="mb-4 inline-block rounded-md border border-ca-surface-border bg-ca-card px-3 py-1.5 text-xs font-bold text-verdict-red">
            {downloadError}
          </p>
        )}

        {feasibility && (feasibility.blockers.length > 0 || feasibility.warnings.length > 0) && (
          <div className="mb-10">
            <FeasibilityPanel
              report={feasibility}
              onFix={(step) => navigate(editLink(step as 1 | 2 | 3 | 4))}
            />
          </div>
        )}

        {latestReport && (
          <Link
            to={`/courses/${course.id}/audit/${latestReport.id}`}
            className="block mb-10 p-5 bg-ca-card rounded-2xl border border-ca-accent/40 hover:border-ca-accent transition"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-1">
                  {t("overview.lastReport")}
                </p>
                <p className="text-ca-surface-ink text-sm">
                  {latestReport.overall_score != null && (
                    <b className="text-ca-surface-accent">{Math.round(latestReport.overall_score)}%</b>
                  )}
                  {latestReport.bender_label && (
                    <> · {t("overview.reachedLevel", { label: latestReport.bender_label })}</>
                  )}
                  <> · {new Date(latestReport.generated_at).toLocaleDateString(dateLocale)}</>
                </p>
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-ca-surface-accent">
                {t("overview.open")}
              </span>
            </div>
          </Link>
        )}

        {/* Zeitlicher Verlauf — nur bei Multi-Session-Formaten */}
        <SessionTimeline sessions={sessions} tlas={tlas} ats={ats} />

        {/* Lernziele */}
        <Section
          title={t("overview.ilosTitle")}
          count={ilos.length}
          onEdit={() => navigate(editLink(2))}
          emptyHint={t("overview.ilosEmpty")}
        >
          {ilos.map((ilo: ILO, idx: number) => (
            <div
              key={ilo.id}
              className="p-5 bg-ca-card rounded-xl border border-ca-surface-border"
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-xs font-black text-ca-surface-muted">{idx + 1}.</span>
                {ilo.action_verb && (
                  <span className="text-xs font-black uppercase tracking-wider text-ca-surface-accent">
                    {ilo.action_verb}
                  </span>
                )}
                {ilo.bender_depth && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-ca-surface-muted px-2 py-0.5 rounded-full bg-ca-card border border-ca-surface-border">
                    {ilo.bender_depth}
                  </span>
                )}
              </div>
              <p className="text-ca-surface-ink leading-relaxed">{ilo.text || t("common.empty")}</p>
            </div>
          ))}
        </Section>

        {/* Methoden */}
        <Section
          title={t("overview.tlasTitle")}
          count={tlas.length}
          onEdit={() => navigate(editLink(3))}
          emptyHint={t("overview.tlasEmpty")}
        >
          {tlas.map((tla: TLA) => (
            <div
              key={tla.id}
              className="p-5 bg-ca-card rounded-xl border border-ca-surface-border"
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-sm font-black text-ca-surface-accent">{tlaLabel(tla.type)}</span>
                {tla.duration_minutes != null && (
                  <span className="text-xs text-ca-surface-muted">~{tla.duration_minutes} min</span>
                )}
                {tla.modality && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-ca-surface-muted px-2 py-0.5 rounded-full bg-ca-card border border-ca-surface-border">
                    {modalityLabels[tla.modality] || tla.modality}
                  </span>
                )}
              </div>
              {tla.description && (
                <p className="text-ca-surface-ink text-sm leading-relaxed mb-2">{tla.description}</p>
              )}
              <LinkedILOsInline linkedIds={tla.linked_ilo_ids} ilosById={ilosById} ilos={ilos} label={t("overview.preparesFor")} />
            </div>
          ))}
        </Section>

        {/* Prüfungen */}
        <Section
          title={t("overview.atsTitle")}
          count={ats.length}
          onEdit={() => navigate(editLink(4))}
          emptyHint={t("overview.atsEmpty")}
        >
          {ats.map((at: AT) => (
            <div
              key={at.id}
              className="p-5 bg-ca-card rounded-xl border border-ca-surface-border"
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-sm font-black text-ca-surface-accent">{atLabel(at.type)}</span>
                {at.format && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-ca-surface-muted px-2 py-0.5 rounded-full bg-ca-card border border-ca-surface-border">
                    {atFormatLabels[at.format] || at.format}
                  </span>
                )}
              </div>
              {at.description && (
                <p className="text-ca-surface-ink text-sm leading-relaxed mb-2">{at.description}</p>
              )}
              <LinkedILOsInline linkedIds={at.linked_ilo_ids} ilosById={ilosById} ilos={ilos} label={t("overview.assesses")} />
            </div>
          ))}
        </Section>

        {/* Trainingsleitfaden — Zeitplan aus den Methoden + CSV-Export */}
        <TrainerGuideCard course={course} tlaLabel={tlaLabel} />

        {/* Meta-Card unten für Komplett-Edit (Step 1) */}
        <div className="mt-12 p-6 bg-ca-card rounded-2xl border border-ca-surface-border flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-ca-surface-muted mb-1">
              {t("overview.editMetaTitle")}
            </p>
            <p className="text-sm text-ca-surface-muted">
              {t("overview.editMetaBody")}
            </p>
          </div>
          <button
            onClick={() => navigate(editLink(1))}
            className="px-5 py-2 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold uppercase tracking-widest hover:border-ca-accent transition"
          >
            {t("overview.editMetaButton")}
          </button>
        </div>
      </div>
    </>
  );
}

// Gesamt-Timeline (F4): Sessions in Positions-Reihenfolge, dazwischen die
// Transfer-Aktivitäten (Phase „between"). Kursweite Transfer-TLAs (ohne
// session_id) zählen in jeder Lücke mit — Transfer findet zwischen jedem
// Termin-Paar statt. Fallback-Dauer 45 min (Backend-Default).
function SessionTimeline({
  sessions,
  tlas,
  ats,
}: {
  sessions: CourseSession[];
  tlas: TLA[];
  ats: AT[];
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const sessionKindLabels = useSessionKindLabels();
  if (sessions.length === 0) return null;
  const betweenTlas = tlas.filter((x) => x.phase === "between");

  const gapInfo = (prevSessionId: string) => {
    const relevant = betweenTlas.filter(
      (x) => !x.session_id || x.session_id === prevSessionId,
    );
    const minutes = relevant.reduce((s, x) => s + (x.duration_minutes ?? 45), 0);
    return { count: relevant.length, hours: minutes / 60 };
  };

  const formatHours = (h: number) =>
    h >= 1 ? t("overview.hoursValue", { value: h.toFixed(1) }) : t("overview.minutesValue", { value: Math.round(h * 60) });

  return (
    <section className="mb-10 p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
      <h2 className="text-2xl font-black text-ca-surface-ink uppercase italic tracking-tighter mb-4">
        {t("overview.timelineTitle")}{" "}
        <span className="text-ca-surface-muted text-base font-bold not-italic">
          ({t("overview.sessionsCount", { count: sessions.length })})
        </span>
      </h2>
      <div className="flex flex-col gap-2">
        {sessions.map((s, idx) => {
          const tlaCount = tlas.filter((x) => x.session_id === s.id).length;
          const atCount = ats.filter((a) => a.session_id === s.id).length;
          const gap = idx < sessions.length - 1 ? gapInfo(s.id) : null;
          return (
            <Fragment key={s.id}>
              <div className="p-4 bg-ca-card rounded-xl border border-ca-surface-border flex flex-wrap items-center gap-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-ca-surface-muted px-2 py-0.5 rounded-full bg-ca-card border border-ca-surface-border">
                  {sessionKindLabels[s.kind] || s.kind}
                </span>
                <span className="font-bold text-ca-surface-ink">{s.title}</span>
                {formatSessionDate(s.date, dateLocale) && (
                  <span className="text-xs text-ca-surface-muted">
                    {formatSessionDate(s.date, dateLocale)}
                  </span>
                )}
                {s.start && s.end && (
                  <span className="text-xs text-ca-surface-muted">
                    {s.start}–{s.end}
                  </span>
                )}
                <span className="ml-auto text-xs text-ca-surface-muted">
                  {t("overview.activitiesCount", { count: tlaCount })} ·{" "}
                  {t("overview.assessmentsCount", { count: atCount })}
                </span>
              </div>
              {gap && gap.count > 0 && (
                <div className="mx-6 px-3 py-1.5 border-l-2 border-dashed border-ca-accent/50 text-xs text-ca-surface-muted">
                  {t("overview.transferGap", {
                    activities: t("overview.activitiesCount", { count: gap.count }),
                    duration: formatHours(gap.hours),
                  })}
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

function Section({  title,
  count,
  onEdit,
  emptyHint,
  children,
}: {
  title: string;
  count: number;
  onEdit: () => void;
  emptyHint: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-black text-ca-ink uppercase italic tracking-tighter">
          {title}{" "}
          <span className="text-ca-ink-muted text-base font-bold not-italic">({count})</span>
        </h2>
        <button
          onClick={onEdit}
          className="px-4 py-2 rounded-md border border-ca-surface-border bg-ca-card text-ca-surface-ink text-xs font-bold uppercase tracking-widest hover:border-ca-accent transition"
        >
          {t("overview.edit")}
        </button>
      </div>
      {count === 0 ? (
        <div className="p-8 bg-ca-card rounded-xl border border-dashed border-ca-surface-border text-center">
          <p className="text-ca-surface-muted italic text-sm">{emptyHint}</p>
        </div>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </section>
  );
}

function LinkedILOsInline({
  linkedIds,
  ilosById,
  ilos,
  label,
}: {
  linkedIds: string[];
  ilosById: Record<string, ILO>;
  ilos: ILO[];
  label: string;
}) {
  const { t } = useTranslation();
  if (ilos.length === 0) return null;
  const linked = linkedIds.map((lid) => ilosById[lid]).filter(Boolean);
  if (linked.length === 0) {
    return (
      <p className="text-[11px] text-ca-surface-muted italic">
        {label}: {t("overview.autoAssign")}
      </p>
    );
  }
  return (
    <p className="text-[11px] text-ca-surface-muted">
      <span className="font-bold uppercase tracking-widest mr-1">{label}:</span>
      {linked
        .map((ilo) => `LZ ${ilo.position + 1}`)
        .join(" · ")}
    </p>
  );
}
